import React, { useEffect } from 'react';
import {
  LayoutDashboard, Users, Flag, Scale, Server, CreditCard,
  Gamepad2, HardDrive, ScrollText, Megaphone, Shield,
} from 'lucide-react';
import { useAdminStore, AdminSection } from '../../store/adminStore';
import AdminDashboard from './AdminDashboard';
import AdminUsers from './AdminUsers';
import AdminReports from './AdminReports';
import AdminAppeals from './AdminAppeals';
import AdminServers from './AdminServers';
import AdminBilling from './AdminBilling';
import AdminGames from './AdminGames';
import AdminErozionGo from './AdminMinecraft';
import AdminAuditLog from './AdminAuditLog';
import AdminAnnouncements from './AdminAnnouncements';

/* ── Navigation items ── */
const NAV_ITEMS: { id: AdminSection; label: string; icon: React.ElementType; badge?: string }[] = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'users', label: 'Utilisateurs', icon: Users },
  { id: 'reports', label: 'Signalements', icon: Flag },
  { id: 'appeals', label: 'Contestations', icon: Scale },
  { id: 'servers', label: 'Serveurs', icon: Server },
  { id: 'billing', label: 'Facturation', icon: CreditCard },
  { id: 'games', label: 'Jeux & Store', icon: Gamepad2 },
  { id: 'minecraft', label: 'Erozion Go', icon: HardDrive },
  { id: 'audit', label: 'Audit Log', icon: ScrollText },
  { id: 'announcements', label: 'Annonces', icon: Megaphone },
];

/* ── Section renderer ── */
const SECTIONS: Record<AdminSection, React.FC> = {
  dashboard: AdminDashboard,
  users: AdminUsers,
  reports: AdminReports,
  appeals: AdminAppeals,
  servers: AdminServers,
  billing: AdminBilling,
  games: AdminGames,
  minecraft: AdminErozionGo,
  audit: AdminAuditLog,
  announcements: AdminAnnouncements,
};

/* ═══════════════════════════════════════════════════════════════════════════ */

const AdminConnect: React.FC = () => {
  const { currentSection, setSection, dashboardStats, fetchDashboard } = useAdminStore();
  const ActiveSection = SECTIONS[currentSection];

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const pendingReports = dashboardStats?.moderation?.pendingReports ?? 0;
  const pendingAppeals = dashboardStats?.moderation?.pendingAppeals ?? 0;

  return (
    <div className="flex h-full w-full overflow-hidden relative">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-red-900/20 via-bg-primary to-bg-primary pointer-events-none" />
      <div className="absolute inset-0 bg-gradient-to-t from-bg-primary via-bg-primary/80 to-transparent pointer-events-none" />
      {/* ── Sidebar Navigation ── */}
      <nav className="relative z-10 w-[220px] flex-shrink-0 bg-bg-secondary/50 border-r border-border-color flex flex-col">
        {/* Brand */}
        <div className="px-4 py-4 flex items-center gap-2.5 border-b border-border-color">
          <div className="w-8 h-8 rounded-lg bg-admin-accent/20 flex items-center justify-center">
            <Shield className="h-4.5 w-4.5 text-admin-accent" />
          </div>
          <div>
            <h1 className="text-sm font-bold text-text-primary leading-none">Admin Connect</h1>
            <p className="text-[10px] text-text-muted mt-0.5">Panneau d'administration</p>
          </div>
        </div>

        {/* Nav items */}
        <div className="flex-1 overflow-y-auto py-2 px-2 space-y-0.5">
          {NAV_ITEMS.map((item) => {
            const isActive = currentSection === item.id;
            const badge =
              item.id === 'reports' && pendingReports > 0 ? String(pendingReports) :
              item.id === 'appeals' && pendingAppeals > 0 ? String(pendingAppeals) :
              undefined;

            return (
              <button
                key={item.id}
                onClick={() => setSection(item.id)}
                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] font-medium transition-all duration-150 ${
                  isActive
                    ? 'bg-admin-accent/15 text-admin-accent'
                    : 'text-text-secondary hover:text-text-primary hover:bg-highlight/50'
                }`}
              >
                <item.icon className="h-4 w-4 flex-shrink-0" />
                <span className="flex-1 text-left truncate">{item.label}</span>
                {badge && (
                  <span className="min-w-[18px] h-[18px] rounded-full bg-error text-white text-[10px] font-bold flex items-center justify-center px-1">
                    {badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </nav>

      {/* ── Content Area ── */}
      <main className="relative z-10 flex-1 overflow-hidden">
        <ActiveSection />
      </main>
    </div>
  );
};

export default AdminConnect;
